import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import Iconify from '../Iconify';
import { baseUrl } from '../../constant/config';
import { PLATFORM_META, formatDate } from './shareMeta';

const PAGE_SIZE = 12;

/**
 * What is actually on the connected account, read live from the Graph API on
 * every load. Posts made outside this app appear here too - anything the
 * account has published is listed exactly as Meta reports it.
 *
 * Meta pages with opaque cursors rather than offsets, so going back means
 * keeping the cursors we have already walked through rather than decrementing
 * a page number.
 */
export default function LivePostsPanel({ platform, onEdit, onDelete }) {
  const meta = PLATFORM_META[platform] || {};

  // cursors[i] is the `after` value that produced page i. The first page has
  // no cursor at all, which is why the stack starts with a single undefined.
  const [cursors, setCursors] = useState([undefined]);
  const [pageIndex, setPageIndex] = useState(0);

  const after = cursors[pageIndex];

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery(
    ['platformPosts', platform, after],
    async () => {
      const params = new URLSearchParams({ platform, limit: PAGE_SIZE });
      if (after) params.append('after', after);
      const { data } = await axios.get(`${baseUrl}/api/videos/platform-posts?${params.toString()}`);
      return data.data;
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      retry: false,
    }
  );

  const posts = data?.posts || [];
  const nextCursor = data?.paging?.next || null;

  const goNext = () => {
    if (!nextCursor) return;
    setCursors((prev) => {
      const next = prev.slice(0, pageIndex + 1);
      next.push(nextCursor);
      return next;
    });
    setPageIndex((i) => i + 1);
  };

  const goBack = () => setPageIndex((i) => Math.max(0, i - 1));

  // Everything the edit and delete dialogs need, including the details the
  // confirmation modal shows so the admin can see which post they are removing.
  const asTarget = (post) => ({
    mode: 'live',
    platform,
    postId: post.id,
    caption: post.caption,
    story: post.story,
    title: post.origin?.debateTitle || null,
    thumbnail: post.thumbnail,
    permalink: post.permalink,
    mediaType: post.mediaType,
    createdTime: post.createdTime,
    likeCount: post.likeCount,
    commentCount: post.commentCount,
    fromDebate: Boolean(post.origin),
  });

  // A configuration or permission problem is the common case here and the
  // message from Meta says exactly what is wrong, so it is shown verbatim.
  const message = error?.response?.data?.message || error?.message;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Live from the {meta.label} Graph API
          {isFetching && !isLoading && ' - refreshing'}
        </Typography>
        <Button
          size="small"
          onClick={() => refetch()}
          disabled={isFetching}
          startIcon={isFetching ? <CircularProgress size={14} /> : <RefreshIcon />}
        >
          Refresh
        </Button>
      </Stack>

      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Could not read {meta.label}</AlertTitle>
          {message}
        </Alert>
      )}

      {isLoading && (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card>
                <Skeleton variant="rectangular" height={160} />
                <CardContent>
                  <Skeleton height={20} />
                  <Skeleton height={20} width="60%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {!isLoading && !isError && posts.length === 0 && (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="subtitle1" gutterBottom>
            No posts on this {meta.label} account
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {meta.label} returned an empty list. Publish a recording from the Recordings page and it
            will appear here.
          </Typography>
        </Card>
      )}

      {!isLoading && !isError && posts.length > 0 && (
        <Grid container spacing={2}>
          {posts.map((post) => (
            <Grid item xs={12} sm={6} md={4} key={post.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {post.thumbnail ? (
                  <CardMedia
                    component="img"
                    height="160"
                    image={post.thumbnail}
                    alt=""
                    sx={{ objectFit: 'cover', bgcolor: 'grey.200' }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 160,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.200',
                      color: 'text.disabled',
                    }}
                  >
                    <MovieOutlinedIcon fontSize="large" />
                  </Box>
                )}

                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    {post.mediaType && (
                      <Chip size="small" label={post.mediaType} variant="outlined" />
                    )}
                    {post.origin && (
                      <Tooltip title={`Published from the recording of "${post.origin.debateTitle}"`}>
                        <Chip size="small" color="primary" variant="outlined" label="from a debate" />
                      </Tooltip>
                    )}
                  </Stack>

                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {post.caption || post.story || (
                      <span style={{ opacity: 0.4 }}>No caption</span>
                    )}
                  </Typography>

                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {formatDate(post.createdTime)}
                  </Typography>
                </CardContent>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 2, pb: 1.5 }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {typeof post.likeCount === 'number' && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <FavoriteBorderIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant="caption">{post.likeCount}</Typography>
                      </Stack>
                    )}
                    {typeof post.commentCount === 'number' && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ChatBubbleOutlineIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant="caption">{post.commentCount}</Typography>
                      </Stack>
                    )}
                    {typeof post.shareCount === 'number' && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ShareOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant="caption">{post.shareCount}</Typography>
                      </Stack>
                    )}
                  </Stack>

                  <Stack direction="row">
                    <Tooltip
                      title={
                        meta.canEditCaption
                          ? 'Edit caption'
                          : 'Instagram captions cannot be edited after publishing'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!meta.canEditCaption}
                          onClick={() =>
                            onEdit(asTarget(post))
                          }
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Open post">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!post.permalink}
                          component={Link}
                          href={post.permalink || undefined}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Delete post">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          onDelete(asTarget(post))
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {!isError && (posts.length > 0 || pageIndex > 0) && (
        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
            Page {pageIndex + 1}
          </Typography>
          <Button
            size="small"
            disabled={pageIndex === 0 || isFetching}
            onClick={goBack}
            startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
          >
            Previous
          </Button>
          <Button
            size="small"
            disabled={!nextCursor || isFetching}
            onClick={goNext}
            endIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}
          >
            Next
          </Button>
        </Stack>
      )}
    </Box>
  );
}

LivePostsPanel.propTypes = {
  platform: PropTypes.oneOf(['facebook', 'instagram']).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
